import {
  getCookie as getCookieLib,
  removeCookie,
  setCookie
} from 'react-use-cookie'

/**
 * Common multi-part TLDs that require special handling.
 * This covers the most frequently used country-code second-level domains (ccSLDs)
 * without requiring the full Public Suffix List database (~30-50KB gzipped).
 */
const MULTI_PART_TLDS = new Set([
  'co.uk',
  'com.au',
  'co.nz',
  'ac.uk',
  'com.br',
  'co.jp',
  'co.in',
  'com.cn',
  'co.za',
  'com.mx',
  'co.kr',
  'com.ar',
  'co.th',
  'com.sg',
  'co.il'
])

/**
 * Extracts the apex domain from a hostname.
 * The apex domain is the main domain without subdomains. This function
 * handles both simple TLDs (.com, .org) and common multi-part TLDs (.co.uk, .com.au).
 *
 * Examples:
 *   - 'www.myrestaurant.com' → 'myrestaurant.com'
 *   - 'order.myrestaurant.com' → 'myrestaurant.com'
 *   - 'orders.grazecraze.com.au' → 'grazecraze.com.au' (handles multi-part TLDs)
 *
 */
export function getApexDomain(hostname: string): string {
  const cleanHostname = hostname.replace(/\.$/, '')

  const parts = cleanHostname.split('.')
  if (parts.length <= 2) {
    return cleanHostname
  }
  const lastTwo = parts.slice(-2).join('.')
  if (MULTI_PART_TLDS.has(lastTwo)) {
    // For multi-part TLDs, return the last 3 parts
    // e.g., 'orders.grazecraze.com.au' → 'grazecraze.com.au'
    return parts.slice(-3).join('.')
  }

  // For standard TLDs, return the last 2 parts
  // e.g., 'www.myrestaurant.com' → 'myrestaurant.com'
  return lastTwo
}

export function getCookie(name: string): string | null {
  const value = getCookieLib(name)
  return value || null
}

export function setToastCookie(
  name: string,
  value: string,
  options: {
    days?: number
    maxAgeSeconds?: number
    domain?: string
    path?: string
    sameSite?: 'Lax' | 'Strict' | 'None'
  } = {}
): void {
  const { days, maxAgeSeconds, domain, path = '/', sameSite = 'Lax' } = options

  const expiryDays =
    days ?? (maxAgeSeconds ? maxAgeSeconds / (60 * 60 * 24) : undefined)

  setCookie(name, value, {
    days: expiryDays,
    domain,
    path,
    SameSite: sameSite
  })
}

export function removeToastCookie(name: string, domain?: string): void {
  if (typeof document === 'undefined') {
    return
  }

  if (domain) {
    // For domain-scoped cookies, we need to explicitly set domain when removing
    document.cookie = `${name}=; max-age=0; path=/; domain=${domain}; SameSite=Lax`
  } else {
    removeCookie(name)
  }
}
